import '../styles/globals.css'
import type { AppProps } from 'next/app'
import { AppWrapper } from '../context/siteMapContext'
import Header from '../components/header/header.component'
import SubPageHeader from '../components/header/subPageHeader.component'
import Footer from '../components/footer.component'
import Menu from '../components/navigation/menu.component'
import { IWebMenu } from '../interfaces/webMenu'
import useFetchWebmenus from '../hooks/useFetchWebmenus'
import useActiveWebmenu from '../hooks/useActiveWebmenu'
import useFetchMainData from '../hooks/useFetchMainData'
import Breadcrumbs from '../components/navigation/breadcrumbs.component'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import useHighContrast from '../hooks/useHighContrast'
import ActionButton from '../components/action-button/action-button.component'
import AccessibilityPopup from '../components/home/popup/accessibilityPopup.component'
import useAccessibilityInfoShown from '../hooks/useAccessibilityInfoShown'
import Script from 'next/script'
import FeaturedNewsPanel from '../components/home/featuredNews/featuredNews.component'
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

function MyApp({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const mainData = useFetchMainData()
  const webmenus = useFetchWebmenus()

  const activePage: IWebMenu | undefined = useActiveWebmenu(webmenus)

  const [highContrastOn, setHighContrastOn] = useHighContrast();
  const [accessibilityShown, setAccessibilityShown] = useAccessibilityInfoShown();

  useEffect(() => {
    CookieConsent.run({
      categories: {
        necessary: {
          enabled: true,  // this category is enabled by default
          readOnly: true  // this category cannot be disabled
        },
        analytics: {}
      },
      guiOptions: {
        consentModal: {
          layout: 'bar inline',
          position: 'bottom center'
        }
      },
      language: {
        default: 'hu',
        translations: {
          hu: {
            consentModal: {
              title: 'Ez a weboldal sütiket használ',
              description: 'Sütiket használunk a tartalmak és hirdetések személyre szabásához, közösségi funkciók biztosításához, valamint weboldalforgalmunk elemzéséhez. Ezenkívül közösségi média-, hirdető- és elemező partnereinkkel megosztjuk az Ön weboldalhasználatra vonatkozó adatait, akik kombinálhatják az adatokat más olyan adatokkal, amelyeket Ön adott meg számukra vagy az Ön által használt más szolgáltatásokból gyűjtöttek.',
              acceptAllBtn: 'Összes süti engedélyezése',
              acceptNecessaryBtn: 'Kizárólag szükséges sütik engedélyezése',
              showPreferencesBtn: 'Testreszabás',
            },
            preferencesModal: {
              title: 'Süti beállítások kezelése',
              acceptAllBtn: 'Összes elfogadása',
              acceptNecessaryBtn: 'Kizárólag szükségesek elfogadása',
              savePreferencesBtn: 'Jelenlegi kijelölés elfogadása',
              closeIconLabel: 'Modal bezárása',
              sections: [
                {
                  title: 'Ez a weboldal sütiket használ',
                  description: 'Sütiket használunk a tartalmak és hirdetések személyre szabásához, közösségi funkciók biztosításához, valamint weboldalforgalmunk elemzéséhez. Ezenkívül közösségi média-, hirdető- és elemező partnereinkkel megosztjuk az Ön weboldalhasználatra vonatkozó adatait, akik kombinálhatják az adatokat más olyan adatokkal, amelyeket Ön adott meg számukra vagy az Ön által használt más szolgáltatásokból gyűjtöttek.'
                },
                {
                  title: 'Elengedhetetlen',
                  description: 'Az elengedhetetlen sütik segítenek használhatóvá tenni a weboldalunkat azáltal, hogy engedélyeznek olyan alapvető funkciókat, mint az oldalon való navigáció és a weboldal biztonságos területeihez való hozzáférés. A weboldal ezen sütik nélkül nem tud megfelelően működni.',
                  //this field will generate a toggle linked to the 'necessary' category
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Statisztikai',
                  description: 'Az adatok névtelen formában való gyűjtésén és jelentésén keresztül a statisztikai sütik segítenek a weboldal tulajdonosának abban, hogy megértse, hogyan lépnek interakcióba a látogatók a weboldallal.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Több információ',
                  description: 'A sütik olyan kis szöveges fájlok, amelyeket egy weboldal felhasználhat arra, hogy még hatékonyabbá tegye a felhasználói élményt. A jogszabályok szerint a sütiket abban az esetben tárolhatjuk az Ön eszközén, ha erre feltétlenül szükség van a weboldalunk működése érdekében. Minden egyéb típusú süti használatához az Ön engedélyére van szükségünk. Jelen weboldal különféle sütiket használ. A weboldalunkon megjelenő némelyik sütit harmadik fél szolgáltatóink helyezik.'
                }
              ]
            }
          }
        }
      }
    });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [router.pathname]);

  useEffect(() => {
    if (sessionStorage.getItem('high-contrast-mode') === '1') {
      setHighContrastOn(true);
    } else {
      setHighContrastOn(false);
    }
  }, [setHighContrastOn]);

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      const isModelShow = !!document.getElementsByClassName('z-modal').length

      if (event.key === "Escape" && !isModelShow && router.pathname !== '/') {
        event.preventDefault();
        router.back();
      }
    };

    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [router]);

  return (
    <>
      <Script id="gtag-manager" async src="https://www.googletagmanager.com/gtag/js?id=GTM-W8XGVMJ"></Script>
      <Script id="gtm-code">{`(function(w,d,s,l,i){w[l] = w[l] || [];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-W8XGVMJ');`}</Script>
      <Script id="gtag-imp">{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments)};
        gtag('js', new Date());

        gtag('config', 'GTM-W8XGVMJ');
        `}
      </Script>
      {/* <Script id="Cookiebot" src="https://cdn.jsdelivr.net/gh/orestbida/cookieconsent@3.1.0/dist/cookieconsent.umd.js"></Script> */}
      <script
        data-cookieconsent="ignore"
        dangerouslySetInnerHTML={{
          __html: `
      
    (function(m, o, n, t, e, r, _){
      m['__GetResponseAnalyticsObject'] = e;m[e] = m[e] || function() {(m[e].q = m[e].q || []).push(arguments)};
      r = o.createElement(n);_ = o.getElementsByTagName(n)[0];r.async = 1;r.src = t;r.setAttribute('crossorigin', 'use-credentials');_.parentNode .insertBefore(r, _);
  })(window, document, 'script', 'https://ga.getresponse.com/script/e1ac1f1b-198e-435a-b8b9-646675bb58e4/ga.js', 'GrTracking');
    
    `
          ,
        }}
      />


      <AppWrapper webmenus={webmenus} mainData={mainData} activePage={activePage} highContrastOn={highContrastOn} setHighContrastOn={setHighContrastOn} accessibilityShown={accessibilityShown} setAccessibilityShown={setAccessibilityShown}>
        {activePage && (
          <>
            <div className={`min-h-[100vh] flex flex-col justify-between ${highContrastOn ? 'highcontrast bg-black' : null}`}>
              <div>
                {router.pathname === '/' ? (
                  <Header {...mainData} />
                ) : (
                  <SubPageHeader {...mainData} activePage={activePage} />
                )}
                {highContrastOn && <div className='h-[6px] bg-white' />}
                <main className={`px-page-side-padding mt-[48px] ${highContrastOn ? 'bg-black' : ''}`}>
                  {activePage.id !== 'HOME' && !!activePage &&
                    <div className='hidden 2xl:block'>
                      <Breadcrumbs activePage={activePage} webmenus={webmenus} />
                    </div>
                  }
                  {activePage.url_HU === '/' && window.location?.pathname === '/' && !!mainData.featuredNews && !!mainData.featuredNews?.length && (
                    <>
                      <FeaturedNewsPanel articles={mainData.featuredNews} />
                      {highContrastOn && (mainData.campaign || mainData.announcements) && <div className='mx-[-22px] h-[6px] bg-white' />}
                    </>
                  )}
                  {!!activePage && <Menu activePage={activePage} />}
                  <Component {...pageProps} />
                </main>
                <ActionButton />
              </div>
              <Footer {...mainData} />
              <AccessibilityPopup show={highContrastOn} />
            </div>
          </>
        )}
      </AppWrapper>
    </>
  )
}

export default MyApp
